import React, { forwardRef, useImperativeHandle, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDatepicker, START_DATE, END_DATE } from '@datepicker-react/hooks';
import { useGlobal } from '@rcg/ui-react';
import DatepickerContext from '@rcg/ui-react/lib/components/DatepickerResponsive/datepickerContext';
import { DEFAULT_DATE_FORMAT_FOR_CALENDAR } from '@rcg/ui-react/lib/utils/constants';
import { buildCalendarStateObject, calculateNumberOfMonths } from '@rcg/ui-react/lib/utils/dates';
import Month from '../Month';

// eslint-disable-next-line react/display-name
const Calendar = forwardRef((props, ref) => {
  const {
    incomingStartDate,
    incomingEndDate,
    minAvailableDate,
    maxAvailableDate,
    startDate,
    endDate,
    numberOfMonths,
    onDateChange,
    monthRef,
    singleDaySelection = false,
  } = props;

  const {
    data: { localizedDayjs },
  } = useGlobal();

  const getStartDate = monthRef
    ? null
    : new Date(localizedDayjs(startDate).format(DEFAULT_DATE_FORMAT_FOR_CALENDAR));

  const [state, setState] = useState({
    startDate: singleDaySelection
      ? new Date(localizedDayjs(startDate).format(DEFAULT_DATE_FORMAT_FOR_CALENDAR))
      : getStartDate,
    endDate: singleDaySelection
      ? new Date(localizedDayjs(startDate).format(DEFAULT_DATE_FORMAT_FOR_CALENDAR))
      : new Date(localizedDayjs(endDate).format(DEFAULT_DATE_FORMAT_FOR_CALENDAR)),
    START_DATE,
  });

  useEffect(() => {
    if (startDate && endDate) {
      setState(
        buildCalendarStateObject(startDate, singleDaySelection ? startDate : endDate, START_DATE),
      );
    }
  }, [startDate, endDate]);

  function handleDateChange(data) {
    if (singleDaySelection) {
      const selectedDate = data.startDate;
      setState({ startDate: selectedDate, endDate: selectedDate });
      onDateChange({ startDate: selectedDate, endDate: selectedDate });
    } else {
      const isBeforeIncomingEndDate = localizedDayjs(data.startDate).isBefore(
        localizedDayjs(incomingEndDate),
      );
      const propertyToUpdate = isBeforeIncomingEndDate ? 'startDate' : 'endDate';
      setState({
        ...state,
        [propertyToUpdate]: data.startDate,
      });
      onDateChange({
        ...state,
        [propertyToUpdate]: data.startDate,
        focusedInput: isBeforeIncomingEndDate ? END_DATE : START_DATE,
      });
    }
  }

  const {
    firstDayOfWeek,
    activeMonths,
    isDateSelected,
    isDateHovered,
    isFirstOrLastSelectedDate,
    isDateBlocked,
    isDateFocused,
    focusedDate,
    onDateHover,
    onDateSelect,
    onDateFocus,
    goToPreviousMonths,
    goToNextMonths,
  } = useDatepicker({
    startDate: state.startDate,
    endDate: state.endDate,
    focusedInput: state.focusedInput,
    onDatesChange: handleDateChange,
    minBookingDate: new Date(
      localizedDayjs(minAvailableDate).format(DEFAULT_DATE_FORMAT_FOR_CALENDAR),
    ),
    maxBookingDate: new Date(
      localizedDayjs(maxAvailableDate).format(DEFAULT_DATE_FORMAT_FOR_CALENDAR),
    ),
    firstDayOfWeek: 7,
    numberOfMonths: numberOfMonths || calculateNumberOfMonths(maxAvailableDate, minAvailableDate),
    initialVisibleMonth: new Date(localizedDayjs(minAvailableDate)),
    changeActiveMonthOnSelect: false,
  });

  useImperativeHandle(ref, () => ({
    previousMonth() {
      goToPreviousMonths();
    },
    nextMonth() {
      goToNextMonths();
    },
  }));

  return (
    <DatepickerContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        focusedDate,
        isDateFocused,
        isDateSelected,
        isDateHovered,
        isDateBlocked,
        isFirstOrLastSelectedDate,
        onDateSelect,
        onDateFocus,
        onDateHover,
        start: state.startDate,
        end: state.endDate,
        incomingStartDate: new Date(
          localizedDayjs(incomingStartDate).format(DEFAULT_DATE_FORMAT_FOR_CALENDAR),
        ),
        incomingEndDate: new Date(
          localizedDayjs(incomingEndDate).format(DEFAULT_DATE_FORMAT_FOR_CALENDAR),
        ),
      }}>
      {activeMonths.map((month) => (
        <Month
          monthRef={
            `${month.year}-${month.month + 1}` === localizedDayjs(startDate).format('YYYY-M') &&
            monthRef
              ? monthRef
              : null
          }
          focusedInput={state.focusedInput}
          key={`${month.year}-${month.month}`}
          year={month.year}
          month={month.month}
          firstDayOfWeek={firstDayOfWeek}
          singleDaySelection={singleDaySelection}
        />
      ))}
    </DatepickerContext.Provider>
  );
});

Calendar.propTypes = {
  incomingStartDate: PropTypes.string,
  incomingEndDate: PropTypes.string,
  minAvailableDate: PropTypes.string,
  maxAvailableDate: PropTypes.string,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  numberOfMonths: PropTypes.number,
  onDateChange: PropTypes.func,
};

Calendar.defaultProps = {
  incomingStartDate: '2021-03-20',
  incomingEndDate: '2021/03/25',
  minAvailableDate: '2021/03/5',
  maxAvailableDate: '2021/04/25',
  startDate: new Date(),
  endDate: new Date(),
  numberOfMonths: 2,
  onDateChange: () => {},
};

export default Calendar;
